import { LitElement, html, css } from 'lit';

/**
 * CrusherCurrencyInput
 *
 * Form atom for entering monetary amounts. Layered over a native text input
 * with `inputmode="decimal"` so mobile keyboards show the right keypad.
 * The visible value is locale-formatted on blur (e.g., "1234.5" → "1,234.50"),
 * raw numeric on focus, and parses to a plain Number on submit via the
 * `value` getter or `change` event.
 *
 * Designed for FundedTax's `amountUsd` field (currently a raw text input with
 * manual decimal validation) and any other "user types a dollar amount" pattern.
 *
 * Attributes:
 *   - value         (string) — initial / current value as a string. Empty when no value.
 *                   Reading the `.value` PROPERTY returns the formatted string;
 *                   reading the `.numericValue` PROPERTY returns a Number (or NaN).
 *   - currency      (string) — ISO 4217 code (default "USD")
 *   - locale        (string) — BCP-47 locale for formatting (default "en-US")
 *   - placeholder   (string) — defaults to a locale-formatted "0.00"
 *   - name          (string) — form field name
 *   - required      (boolean)
 *   - disabled      (boolean)
 *   - min           (number) — minimum allowed value (inclusive)
 *   - max           (number) — maximum allowed value (inclusive)
 *   - step          (number) — increment for input controls (default 0.01)
 *   - invalid       (boolean) — visually invalid state (for the consumer's validation flow)
 *   - prefix-symbol (boolean) — show the currency symbol as a prefix inside the input (default: true)
 *
 * Events:
 *   - change — fires on blur after formatting. `event.detail = { value: string, numericValue: number | null, valid: boolean }`.
 *   - input  — fires on every keystroke (no formatting). Same detail shape.
 *
 * A11y: the inner <input> stays a real form input — works with native form
 * submission. Currency symbol is `aria-hidden` (announced via the input's
 * label which the consumer supplies via crusher-field or aria-label).
 */
export class CrusherCurrencyInput extends LitElement {
  static properties = {
    value:        { type: String, reflect: true },
    currency:     { type: String, reflect: true },
    locale:       { type: String, reflect: true },
    placeholder:  { type: String, reflect: true },
    name:         { type: String, reflect: true },
    required:     { type: Boolean, reflect: true },
    disabled:     { type: Boolean, reflect: true },
    min:          { type: Number, reflect: true },
    max:          { type: Number, reflect: true },
    step:         { type: Number, reflect: true },
    invalid:      { type: Boolean, reflect: true },
    prefixSymbol: { type: Boolean, attribute: 'prefix-symbol', reflect: true },
    _focused:     { state: true }
  };

  constructor() {
    super();
    this.value = '';
    this.currency = 'USD';
    this.locale = 'en-US';
    this.placeholder = '';
    this.name = '';
    this.required = false;
    this.disabled = false;
    this.min = null;
    this.max = null;
    this.step = 0.01;
    this.invalid = false;
    this.prefixSymbol = true;
    this._focused = false;
  }

  static styles = css`
    :host {
      display: inline-flex;
      align-items: stretch;
      width: 100%;
      max-width: 100%;
      position: relative;
    }

    .wrapper {
      display: inline-flex;
      align-items: stretch;
      width: 100%;
      background: var(--crusher-surface);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-md);
      transition: border-color var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard),
                  box-shadow var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard);
    }
    :host([invalid]) .wrapper {
      border-color: var(--crusher-feedback-danger);
    }
    :host(:focus-within) .wrapper {
      border-color: var(--crusher-color-brand-primary);
      box-shadow: 0 0 0 var(--crusher-focus-width) color-mix(in srgb, var(--crusher-color-brand-primary) 22%, transparent);
    }
    :host([disabled]) .wrapper {
      opacity: var(--crusher-opacity-disabled);
      cursor: not-allowed;
    }

    .prefix {
      display: inline-flex;
      align-items: center;
      padding: 0 var(--crusher-spacing-2) 0 var(--crusher-spacing-3);
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      user-select: none;
      pointer-events: none;
    }

    input {
      flex: 1;
      min-width: 0;
      appearance: none;
      background: transparent;
      border: none;
      outline: none;
      color: var(--crusher-fg);
      font: inherit;
      font-size: var(--crusher-font-size-sm);
      font-variant-numeric: tabular-nums;
      padding: var(--crusher-spacing-2) var(--crusher-spacing-3);
      text-align: right;
      width: 100%;
    }
    :host([disabled]) input {
      cursor: not-allowed;
    }
    input::placeholder {
      color: var(--crusher-fg-muted);
      opacity: 0.6;
    }
  `;

  /* Numeric helpers — parse + format */

  _currencySymbol() {
    try {
      const parts = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'narrowSymbol'
      }).formatToParts(0);
      return parts.find((p) => p.type === 'currency')?.value ?? this.currency;
    } catch {
      return this.currency;
    }
  }

  _parse(raw) {
    if (raw == null || raw === '') return null;
    /* Strip everything that isn't a digit, decimal separator, or minus.
       Locale-aware: en-US uses ',' as thousands + '.' as decimal; de-DE
       flips. We use the locale to derive the decimal separator. */
    let decimalSeparator = '.';
    try {
      const parts = new Intl.NumberFormat(this.locale).formatToParts(1.1);
      decimalSeparator = parts.find((p) => p.type === 'decimal')?.value ?? '.';
    } catch { /* fall through with '.' default */ }

    let cleaned = String(raw).trim();
    /* Remove all chars except digits, sign, and the locale decimal separator. */
    const allowed = new Set(['-', decimalSeparator, ...Array.from('0123456789')]);
    cleaned = Array.from(cleaned).filter((ch) => allowed.has(ch)).join('');
    /* Normalize the decimal separator to '.' for Number parsing */
    if (decimalSeparator !== '.') cleaned = cleaned.replace(decimalSeparator, '.');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
  }

  _format(num) {
    if (num == null || !Number.isFinite(num)) return '';
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    } catch {
      return num.toFixed(2);
    }
  }

  get numericValue() {
    return this._parse(this.value);
  }

  _isValid(num) {
    if (this.required && num == null) return false;
    if (num == null) return !this.required;
    if (this.min != null && num < this.min) return false;
    if (this.max != null && num > this.max) return false;
    return true;
  }

  _emit(eventName) {
    const num = this._parse(this.value);
    this.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        value: this.value,
        numericValue: num,
        valid: this._isValid(num)
      },
      bubbles: true,
      composed: true
    }));
  }

  _onFocus() {
    this._focused = true;
    /* Show the raw numeric value while focused for cleaner editing.
       The format-on-blur happens in _onBlur. */
    const num = this._parse(this.value);
    if (num != null) {
      this.value = String(num);
    }
  }

  _onBlur() {
    this._focused = false;
    const num = this._parse(this.value);
    if (num != null) {
      this.value = this._format(num);
    }
    this._emit('change');
  }

  _onInput(e) {
    /* While focused, keep the raw user input as-is so typing isn't disrupted */
    this.value = e.target.value;
    this._emit('input');
  }

  _displayValue() {
    if (this._focused) return this.value;
    const num = this._parse(this.value);
    return num != null ? this._format(num) : this.value;
  }

  _placeholder() {
    if (this.placeholder) return this.placeholder;
    return this._format(0);
  }

  render() {
    return html`
      <div class="wrapper">
        ${this.prefixSymbol ? html`<span class="prefix" aria-hidden="true">${this._currencySymbol()}</span>` : null}
        <input
          type="text"
          inputmode="decimal"
          name=${this.name}
          .value=${this._displayValue()}
          placeholder=${this._placeholder()}
          ?required=${this.required}
          ?disabled=${this.disabled}
          step=${this.step}
          aria-invalid=${this.invalid ? 'true' : 'false'}
          @focus=${this._onFocus}
          @blur=${this._onBlur}
          @input=${this._onInput} />
      </div>
    `;
  }
}

customElements.define('crusher-currency-input', CrusherCurrencyInput);
